<template>
    <div class="edit-user">

            <div class="card">
                <div class="card-header">
                    <h5 class="card-title text-center" v-if="!user.AppUserId">Add User</h5>
                    <h5 class="card-title text-center" v-if="user.AppUserId">Edit User</h5>
                    <hr>
                </div>
                <div class="card-body">
                    <div class="row mb-2">
                        <BasicTextInput label="First Name"
                                        v-model="user.FirstName"
                                        :validator="$v.user.FirstName"
                                        :validatorMsgMap="validatorMsgMapBase"
                                        class="col-6" />
                        <BasicTextInput label="Last Name"
                                        v-model="user.LastName"
                                        :validator="$v.user.LastName"
                                        :validatorMsgMap="validatorMsgMapBase"
                                        class="col-6" />
                    </div>

                    <div class="row mb-2">
                        <PhoneNumberInput label="Phone" class="col-6" v-model="user.MobilePhone" />
                        <BasicTextInput   label="Email"
                                          class="col-6"
                                          :validator="$v.user.Email"
                                          :validatorMsgMap="validatorMsgMapBase"
                                          v-model="user.Email"       />
                    </div>

                    <div class="row mb-2">
                        <BasicTextInput label="Address" class="col-8" v-model="user.AddressLine1" />
                        <BasicTextInput label="Suite"   class="col-4" v-model="user.AddressLine2" />
                    </div>

                    <div class="row mb-2">
                        <BasicTextInput label="City"         class="col-4"  v-model="user.City"/>
                        <StateSelect    label="Select State" class="col-4"  v-model="user.StateProvinceId" />
                        <ZipCodeInput   label="Zip Code"     class="col-4"  v-model="user.ZipCode" />
                    </div>

                    <div class="row mb-2">
                        <RoleSelect    v-model.number="user.UserRoleId"
                                       :disabled="userPersisted"
                                       :roles="roles"
                                       class="col-6" label="Role" />
                        <ManagerSelect v-model="user.ManagerId"
                                       class="col-6"
                                       label="Manager"
                                       :disabled="selectedUserIsNotSalesRep"
                                       v-if="hasManagerSelectAuthority"
                                       :potentialManagers="potentialManagers"/>
                    </div>

                    <div class="row mb-2">
                        <div class="col-12">
                            <label><b>Groups</b></label>
                            <div class="form-group">
                                <v-select label="Name"
                                          v-model="user.GroupIds"
                                          multiple
                                          :options="groupOptions" :reduce="group => group.Id"
                                          class="group-select" />
                            </div>
                        </div>
                    </div>

                    <div class="row mb-2" v-if="can('access-user-lockout')">
                        <div class="col-6">
                            <div class="d-flex justify-content-center  align-items-center">
                                <div class="mr-2 user-locked-state">User Status: <span :class="lockoutStatusClass">{{ lockoutStatus }}</span></div>
                                <ToggleSwitch v-model="user.IsLockedOut" />
                            </div>
                        </div>
                    </div>

                    <div class="mt-4">
                        <button class="btn btn-primary btn-block" v-if="!user.AppUserId" @click="createUser">Create User</button>
                        <button class="btn btn-primary btn-block" v-if="user.AppUserId"  @click="updateUser">Update User</button>
                    </div>
                </div>
            </div>

    </div>
</template>

<script>
    import { UserService }         from '@/services';
    import { mapState }            from 'vuex';
    import { validatorMsgMapBase } from "@/modules/helpers";
    import {
        required,
        email,
    }                              from 'vuelidate/lib/validators';
    import {
        userRoleEnum,
        getKeyByValue
    }                              from "../../../modules/helpers";

    export default {
        name: "DealerUsersEdit",
        data: () => {
            return {
                validatorMsgMapBase: validatorMsgMapBase,
            }
        },
        props: {
           user: {
               type: Object,
               default: () => {
                   return {
                       FirstName: '',
                       LastName:  '',
                       MobilePhone: '',
                       Email: '',
                       AddressLine1 : '',
                       AddressLine2 :  '',
	                   City: '',
                       StateProvinceId : null,
	                   ZipCode : null,
                       UserRoleId: null,
                       ManagerId: null,
                       GroupIds: [],
                       IsLockedOut: false,
                   }
               }
           },
           users: {
               type: Array,
           },
           groups: {
               type: Array,
           }
        },
        computed: {
            ...mapState(['currentUser']),
            potentialManagers(){
                // only sales reps will have managers, and only Sales Manager will be their managers, so just return a list of sales managers...
                return this.users.filter(du => du.UserRoleId === 3); // 3 === 'Sales Manager'
            },
            selectedUserIsNotSalesRep(){
                return this.user.UserRoleId !== 4;
            },
            userPersisted(){
                return !!this.user.AppUserId;
            },
            hasManagerSelectAuthority(){
                let role = getKeyByValue(userRoleEnum,this.currentUser.UserRoleId);
                if ( role === 'SmartSales Admin'   ) return true;
                if ( role === 'Sales Administrator') return true;
                return false;
            },
            groupOptions(){
                return this.groups.sort(function(a, b) {
                    var textA = a.RegionCode.toUpperCase();
                    var textB = b.RegionCode.toUpperCase();
                    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
                });
            },
            roles(){
                let role = getKeyByValue(userRoleEnum,this.currentUser.UserRoleId);

                if (role === 'SmartSales Admin') {
                    return Object.entries(userRoleEnum).filter(([k,v]) => v >=  2); // can create Sales Administrators and Lesser Users
                } else if (role === 'Sales Administrator'){
                    return Object.entries(userRoleEnum).filter(([k,v]) => v >=  2); // can create Sales Administrators and Lesser Users
                } else if (role === 'Sales Manager') {
                    return Object.entries(userRoleEnum).filter(([k,v]) => v === 4); // can create Sales Reps
                }

            },
            lockoutStatusClass(){
                return this.user.IsLockedOut ? 'text-danger' : 'text-success';
            },
            lockoutStatus(){
                return this.user.IsLockedOut ? 'LOCKED' : 'UNLOCKED' ;
            }
        },
        methods: {
            updateUser(){
                this.$v.user.$touch();
                if (this.$v.user.$invalid){
                    this.$store.dispatch('ADD_NOTIFICATION', { text: 'See Validation Errors', type: 'error' });
                    return;
                }

                let user = this.handleManagerSetup(this.user);

                // let promises = [];
                // if (user.IsLockedOut){
                //     promises.push(UserService.lock(user.AppUserId))
                // } else {
                //     promises.push(UserService.unlock(user.AppUserId))
                // }

                // promises.push(UserService.update(user));

                // Promise.all(promises)
                UserService.update(user).then(res => {
                    console.log('res', res)
                    this.$store.dispatch('ADD_NOTIFICATION', { text: 'User Updated', type: 'success'});
                    this.$emit('update:success');
                }).catch(err => {
                    this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message, type: 'error'});
                })
            },
            createUser(){
                this.$v.user.$touch();
                if (this.$v.user.$invalid){
                    this.$store.dispatch('ADD_NOTIFICATION', { text: 'See Validation Errors', type: 'error' });
                    return;
                }

                let user = this.handleManagerSetup(this.user);

                UserService.create(this.$route.params.id, user).then(res => {
                    this.$store.dispatch('ADD_NOTIFICATION', { text: 'User Created', type: 'success'});
                    this.$emit('update:success');
                }).catch(err => {
                    this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message, type: 'error'});
                })
            },
            handleManagerSetup(user){
                let currentUserRole = getKeyByValue(userRoleEnum,this.currentUser.UserRoleId);
                if (currentUserRole === 'Sales Manager'){
                    user.ManagerId = this.currentUser.SalesRepId;
                }
                if (user.ManagerId === 0){
                    user.ManagerId = null;
                }

                return user;
            },
        },
        validations: {
            user: {
                FirstName: {
                    required,
                },
                LastName: {
                    required,
                },
                Email: {
                    required,
                    email,
                }
            },
        },
        watch: {
            'user.UserRoleId': function (newRoleId,oldRoleId){
                if (newRoleId !== 4){
                    this.$nextTick(() => {
                        this.user.ManagerId = null;
                    })
                }
            }

        }
    }
</script>

<style scoped lang="scss">
    .edit-user {
        min-width: 600px;
        // max-width: 1800px;
        .group-select {
            background-color: $white;
        }
    }
    .user-locked-state {
        min-width: 170px;
    }
</style>
